/**
 * User Preferences localStorage Service
 *
 * Provides abstraction for storing user preferences in localStorage.
 * Designed to be replaced with Session Service in future iterations.
 *
 * @module userPreferencesStorage
 */

import type { PreviewOptions } from '@core/domain/PreviewOptions';
import { DEFAULT_PREVIEW_OPTIONS } from '@core/domain/PreviewOptions';
import { logger } from '@core/logging';

/**
 * localStorage key for user preferences
 * @constant
 */
const STORAGE_KEY = 'soteria-user-preferences';

/**
 * Current schema version for preferences migration
 * Increment this when making breaking changes to the preferences structure
 * @constant
 */
const CURRENT_VERSION = 2;

/**
 * Favorite item types
 */
export type FavoriteItemType = 'community' | 'page';

/**
 * Represents a single favorite item (community or page)
 */
export interface FavoriteItem {
  /** Unique identifier */
  id: string;
  /** Type of favorite */
  type: FavoriteItemType;
  /** Display label */
  label: string;
  /** Full route path for navigation */
  route: string;
  /** Parent community ID (for page favorites) */
  communityId?: string;
  /** Community logo URL (for community favorites) */
  iconUrl?: string;
  /** ISO timestamp when added */
  addedAt: string;
}

/**
 * Stored preferences structure with versioning for migrations
 */
export interface StoredUserPreferences {
  /** Schema version for future migrations */
  version: number;
  /** User's preferred home page route, or null for default */
  homePage: string | null;
  /** List of favorite items */
  favorites: FavoriteItem[];
  /** Preview effect toggle states for moderator well-being controls */
  previewOptions?: PreviewOptions;
  /** User-chosen avatar palette color index, or null for hash-based default */
  avatarColorIndex?: number | null;
  /** ISO timestamp of last update */
  updatedAt: string;
}

/**
 * Default preferences when none exist
 */
const DEFAULT_PREFERENCES: StoredUserPreferences = {
  version: CURRENT_VERSION,
  homePage: null,
  favorites: [],
  updatedAt: new Date().toISOString(),
};

/**
 * Type guard to validate a FavoriteItem structure at runtime
 */
// eslint-disable-next-line complexity
function isValidFavoriteItem(item: unknown): item is FavoriteItem {
  if (typeof item !== 'object' || item === null) {
    return false;
  }

  const obj = item as Record<string, unknown>;

  return (
    typeof obj.id === 'string' &&
    (obj.type === 'community' || obj.type === 'page') &&
    typeof obj.label === 'string' &&
    typeof obj.route === 'string' &&
    typeof obj.addedAt === 'string' &&
    (obj.communityId === undefined || typeof obj.communityId === 'string') &&
    (obj.iconUrl === undefined || typeof obj.iconUrl === 'string')
  );
}

/**
 * Type guard to validate a PreviewOptions structure at runtime
 */
function isValidPreviewOptions(value: unknown): value is PreviewOptions {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const obj = value as Record<string, unknown>;

  return typeof obj.blur === 'boolean' && typeof obj.gray === 'boolean' && typeof obj.flip === 'boolean';
}

/**
 * Type guard to validate StoredUserPreferences structure at runtime
 */
// eslint-disable-next-line complexity
function isValidStoredPreferences(data: unknown): data is Partial<StoredUserPreferences> {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  // Check version if present
  if (obj.version !== undefined && typeof obj.version !== 'number') {
    return false;
  }

  // Check homePage if present
  if (obj.homePage !== undefined && obj.homePage !== null && typeof obj.homePage !== 'string') {
    return false;
  }

  // Check favorites if present
  if (obj.favorites !== undefined) {
    if (!Array.isArray(obj.favorites)) {
      return false;
    }

    // Validate each favorite item
    if (!obj.favorites.every(isValidFavoriteItem)) {
      return false;
    }
  }

  // Check previewOptions if present — strip invalid values so migration can add defaults
  if (obj.previewOptions !== undefined && !isValidPreviewOptions(obj.previewOptions)) {
    delete obj.previewOptions;
  }

  // Check avatarColorIndex if present — strip invalid values so defaults apply
  if (obj.avatarColorIndex !== undefined && obj.avatarColorIndex !== null && typeof obj.avatarColorIndex !== 'number') {
    delete obj.avatarColorIndex;
  }

  // Check updatedAt if present
  if (obj.updatedAt !== undefined && typeof obj.updatedAt !== 'string') {
    return false;
  }

  return true;
}

/**
 * Migrate preferences from older versions if needed
 */
function migratePreferences(stored: Partial<StoredUserPreferences>): StoredUserPreferences {
  return {
    version: CURRENT_VERSION,
    homePage: stored.homePage ?? null,
    favorites: stored.favorites ?? [],
    // v1→v2: Add previewOptions with defaults when missing
    previewOptions: stored.previewOptions ?? { ...DEFAULT_PREVIEW_OPTIONS },
    avatarColorIndex: stored.avatarColorIndex ?? null,
    updatedAt: stored.updatedAt ?? new Date().toISOString(),
  };
}

/**
 * Load user preferences from localStorage
 * Returns default preferences if none exist, validation fails, or on parse error
 */
export function loadPreferences(): StoredUserPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      return { ...DEFAULT_PREFERENCES };
    }

    const parsed: unknown = JSON.parse(stored);

    // Validate the parsed data structure
    if (!isValidStoredPreferences(parsed)) {
      logger.warn('Invalid user preferences format in localStorage, using defaults');

      return { ...DEFAULT_PREFERENCES };
    }

    // Migrate if needed
    if (!parsed.version || parsed.version < CURRENT_VERSION) {
      const migrated = migratePreferences(parsed);

      savePreferences(migrated);

      return migrated;
    }

    return migratePreferences(parsed);
  } catch (error) {
    // On any error, return defaults
    logger.warn('Failed to load user preferences from localStorage', {
      error: error instanceof Error ? error.message : String(error),
    });

    return { ...DEFAULT_PREFERENCES };
  }
}

/**
 * Save user preferences to localStorage
 */
export function savePreferences(preferences: StoredUserPreferences): void {
  try {
    const toStore: StoredUserPreferences = {
      ...preferences,
      version: CURRENT_VERSION,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  } catch (error) {
    logger.error('Failed to save user preferences to localStorage', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Clear all user preferences from localStorage
 */
export function clearPreferences(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.error('Failed to clear user preferences from localStorage', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Update only the home page preference
 */
export function setHomePagePreference(route: string | null): void {
  const current = loadPreferences();

  savePreferences({
    ...current,
    homePage: route,
  });
}

/**
 * Add a favorite item
 * Returns false if item already exists
 */
export function addFavoriteItem(item: Omit<FavoriteItem, 'addedAt'>): boolean {
  const current = loadPreferences();

  // Check if already exists
  if (current.favorites.some(f => f.id === item.id)) {
    return false;
  }

  savePreferences({
    ...current,
    favorites: [
      ...current.favorites,
      {
        ...item,
        addedAt: new Date().toISOString(),
      },
    ],
  });

  return true;
}

/**
 * Remove a favorite item by ID
 * Returns false if item wasn't found
 */
export function removeFavoriteItem(id: string): boolean {
  const current = loadPreferences();
  const initialLength = current.favorites.length;

  savePreferences({
    ...current,
    favorites: current.favorites.filter(f => f.id !== id),
  });

  return current.favorites.length !== initialLength;
}

/**
 * Update only the preview options preference
 */
export function setPreviewOptions(options: PreviewOptions): void {
  const current = loadPreferences();

  savePreferences({
    ...current,
    previewOptions: options,
  });
}

/**
 * Update only the avatar color index preference
 */
export function setAvatarColorIndex(index: number | null): void {
  const current = loadPreferences();

  savePreferences({
    ...current,
    avatarColorIndex: index,
  });
}
